import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { capitalize } from 'lodash';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useReactToPrint } from 'react-to-print';
import {
  PermissoesService,
  RecomendacaoSemeaduraService,
  SafrasService,
} from '../../services';
import {
  Talhao,
  RecomendacaoSemeaduraSimple,
  RecomendacaoSemeaduraStatusType,
  Safra,
  LoadedRecomendacaoSemeaduraStatus,
} from '../../@types';
import { default as SituacaoFieldComponent } from './situacao-field';
import ActionButtons, { ActionButtonsProps } from './action-buttons';
import { FilterRecomendacoesType } from '../../services/recomendacoes-semeadura';
import Agro1Toast from '../../components/Agro1Toast';
import useLocalStore from '../../hooks/use-local-store';
import usePrintRecomendacaoStore from './store';
import { SkeletonColumn } from '../../components/SkeletonTable/types';
import SkeletonTable from '../../components/SkeletonTable';
import Printable from './printable';

type StatusFilter = RecomendacaoSemeaduraStatusType | 'todos';

type StatusesType = {
  value: StatusFilter;
  label: string;
}[];

const statusParse = {
  publicado: 'Publicado',
  rascunho: 'Rascunho',
} as const;

const skeletonTableColumns: SkeletonColumn[] = [
  { header: '#' },
  { header: 'Data início' },
  { header: 'Atividade' },
  { header: 'Talhões' },
  { header: 'Recomendante', className: 'col-3' },
  { header: 'Situação' },
  { header: 'Status', className: 'col-2' },
  { header: 'Ações' },
];

const statusField = (item: RecomendacaoSemeaduraSimple) => {
  const situacaoParsed = statusParse[item.status];

  return <div className="status">{situacaoParsed}</div>;
};

const acoesField = (props: ActionButtonsProps) => <ActionButtons {...props} />;

const statusesFilter: StatusesType = [
  { value: 'todos', label: 'Todos' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'publicado', label: 'Publicado' },
];

const glebasDescricoesParsed = (item: RecomendacaoSemeaduraSimple) =>
  item.glebas.map((gleba: Talhao) => capitalize(gleba.descricao)).join(', ');

const dataInicioParsed = (item: RecomendacaoSemeaduraSimple) =>
  dayjs(item.dataInicio).format('DD/MM/YYYY');

const RecomendacoesSemeaduraPage = () => {
  const [loadedStatuses, setLoadedStatuses] = useState<
    LoadedRecomendacaoSemeaduraStatus[]
  >([]);
  const currentPropriedade = useLocalStore('currentPropriedade').value;
  const dataToPrint = usePrintRecomendacaoStore((store) => store.dataToPrint);
  const clearPrintData = usePrintRecomendacaoStore((state) => state.clear);
  const [safras, setSafras] = useState<Safra[]>([]);
  const navigate = useNavigate();

  const currentSafraId = useMemo(
    () => safras.find((safra) => safra.current)?.id,
    [safras],
  );

  const [safraFilter, setSafraFilter] = useState<number>();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [safraLoading, setSafraLoading] = useState(false);
  const [recomendacoesSemeaduraLoading, setRecomendacoesSemeaduraLoading] =
    useState(false);
  const showLoading = recomendacoesSemeaduraLoading || safraLoading;
  const [recomendacoesSemeadura, setRecomendacoesSemeadura] = useState<
    RecomendacaoSemeaduraSimple[]
  >([]);

  const printRef = useRef(null);

  const triggerPrint = useReactToPrint({
    onAfterPrint: () => clearPrintData(),
    content: () => printRef.current,
  });

  const filter = () => {
    if (!safraFilter) return;
    filterRecomendacoesSemeadura({
      safra: safraFilter,
      status: statusFilter === 'todos' ? undefined : statusFilter,
      propriedade: currentPropriedade?.id,
    });
  };

  useEffect(() => {
    const loadSafras = async () => {
      try {
        setSafraLoading(true);
        setSafras([]);
        setSafras(await SafrasService.list());
      } catch (error) {
        Agro1Toast.show({
          detail: 'Falha ao buscar safras.',
          severity: 'error',
          sticky: true,
        });
        console.error(error);
      } finally {
        setSafraLoading(false);
      }
    };

    loadSafras();
  }, []);

  const listReloader = useCallback(async () => {
    const params = {
      safra: safraFilter ?? currentSafraId,
      propriedade: currentPropriedade?.id,
      status: statusFilter === 'todos' ? undefined : statusFilter,
    };

    setRecomendacoesSemeadura(await RecomendacaoSemeaduraService.list(params));
  }, [safraFilter, currentPropriedade, statusFilter, currentSafraId]);

  const filterRecomendacoesSemeadura = useCallback(
    async (params: FilterRecomendacoesType) => {
      try {
        setRecomendacoesSemeaduraLoading(true);
        setRecomendacoesSemeadura([]);

        setRecomendacoesSemeadura(
          await RecomendacaoSemeaduraService.list(params),
        );
      } catch (error) {
        Agro1Toast.show({
          detail: 'Falha ao buscar recomendacoes semeadura.',
          severity: 'error',
          sticky: true,
        });

        console.error(error);
      } finally {
        setRecomendacoesSemeaduraLoading(false);
      }
    },
    [],
  );

  const SituacaoField = useCallback(
    (item: RecomendacaoSemeaduraSimple) =>
      SituacaoFieldComponent({
        item,
        loadedSituacoes: loadedStatuses,
        loadedSituacoesSetter: setLoadedStatuses,
      }),
    [setLoadedStatuses, loadedStatuses],
  );

  useEffect(() => {
    if (!currentSafraId) return;

    filterRecomendacoesSemeadura({
      safra: safraFilter ?? currentSafraId,
      propriedade: currentPropriedade?.id,
      status: statusFilter === 'todos' ? undefined : statusFilter, // nao adcionado na dependencia pois nao queremos dar trigger quando o cara troca o status
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRecomendacoesSemeadura, currentPropriedade, currentSafraId]);

  useEffect(() => {
    setSafraFilter(currentSafraId);
  }, [currentSafraId]);

  useEffect(() => {
    if (!dataToPrint.talhoes.length) return;

    triggerPrint();
  }, [dataToPrint, triggerPrint]);

  return (
    <div className="recomendacoes-semeadura-page">
      {/* Foi necessário colocar o ConfirmDialog aqui e não no action-buttons.tsx para ele não abrir multiplas vezes */}
      <ConfirmDialog />
      <div className="flex flex-wrap mt-6 mx-3">
        <div className="text-3xl">Recomendação de Semeadura</div>
        {PermissoesService.recomendacaoSemeadura.manage && (
          <Button
            className="button-secondary ml-auto"
            label="+ Novo"
            severity="success"
            onClick={() => navigate('/recomendacoes-semeadura/create')}
          />
        )}
      </div>
      <hr />
      <div className="col-10 col-offset-1">
        <div className="flex flex-wrap align-items-center mt-5">
          <div>
            Safra:
            <Dropdown
              value={safraFilter}
              onChange={(e) => setSafraFilter(e.target.value)}
              options={safras}
              optionLabel="descricao"
              optionValue="id"
              placeholder="Safra"
              className="w-full md:w-20rem mx-2"
            />
          </div>
          <div className="mx-4">
            Status:
            <Dropdown
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={statusesFilter}
              optionLabel="label"
              optionValue="value"
              placeholder="Status"
              className="w-full md:w-20rem mx-2"
            />
          </div>
          <Button
            label="Filtrar"
            className="ml-3 button-primary"
            onClick={filter}
          />
        </div>
      </div>
      <hr />
      <div className="col-11 mt-5 m-auto">
        {showLoading && <SkeletonTable columns={skeletonTableColumns} />}
        {showLoading || (
          <DataTable
            value={recomendacoesSemeadura}
            emptyMessage="Nenhuma recomendação de semeadura encontrada."
            paginator
            rows={10}
            className="mt-5 p-0 data-table"
          >
            <Column field="id" header="#" className="col" />
            <Column
              body={dataInicioParsed}
              header="Data início"
              className="col"
            />
            <Column
              field="atividade.descricao"
              header="Atividade"
              className="col"
              align="center"
            />
            <Column
              field="periodo.descricao"
              header="Período"
              className="col"
            />
            <Column
              body={glebasDescricoesParsed}
              header="Talhões"
              className="col-3"
            />
            <Column
              field="usuario.nomeCompleto"
              header="Recomendante"
              className="col"
            />
            <Column body={SituacaoField} header="Situação" className="col " />
            <Column body={statusField} header="Status" className="col-1" />
            {(PermissoesService.recomendacaoSemeadura.manage ||
              PermissoesService.recomendacaoSemeadura.editDelete) && (
              <Column
                body={(item) => acoesField({ item, safras, listReloader })}
                header="Ações"
                className="col acoes"
                align="center"
              />
            )}
          </DataTable>
        )}
      </div>
      <Printable data={dataToPrint} ref={printRef} />
    </div>
  );
};

export default RecomendacoesSemeaduraPage;
